// Generic Types for Form_6 - Configurable Table Form

export interface Section {
  id: string;
  pageComponentId: string;
  componentId?: string;
  type: string;
  variant: string;
  title: string;
  order: number;
  data?: any;
}

export interface FormProps {
  selectedSection: Section;
  onSave: (updatedSection: Section) => void;
  pageId?: string;
  pageComponentId?: string;
  variantDetails?: VariantDetails;
}

// Generic configuration for Form_6
export interface Form6Config {
  // Labels
  tabsLabel?: string; // "Fee Types", "Categories", etc.
  tabsSingularLabel?: string; // "Fee Type", "Category"
  rowsLabel?: string; // "Rows", "Categories"
  rowsSingularLabel?: string; // "Row", "Category"
  columnsLabel?: string; // "Columns", "Semesters"
  columnsSingularLabel?: string; // "Column", "Semester"
  categoryHeaderLabel?: string; // Default text for first column header
  
  // Icons/Emojis
  settingsIcon?: string;
  tabsIcon?: string;
  
  // Feature flags
  showTabStyling?: boolean; // Show text/bg/active bg colors for tabs
  showRowStyling?: boolean; // Show colors for rows
  showColumnStyling?: boolean; // Show colors for columns
  showCellStyling?: boolean; // Show colors for cells
  showCategoryHeader?: boolean; // Show/edit category header
  
  // Granular color control for InlineEditor
  showBorderColor?: boolean; // Show border color picker in InlineEditor (default: true)
  showTextColor?: boolean; // Show text color picker in InlineEditor (default: true)
  showBgColor?: boolean; // Show background color picker in InlineEditor (default: true)
}

export interface VariantDetails extends Form6Config {
  componentFields?: any[];
  selector?: (state: any) => any;
  setAction?: (data: any) => any;
  
  // API Methods
  getMethod?: (id: number) => Promise<any>;
  createMethod?: (data: any) => Promise<any>;
  updateMethod?: (id: number, data: any) => Promise<any>;
  
  // Tab (Fee Type) Methods
  createTabMethod?: (data: any) => Promise<any>;
  updateTabMethod?: (id: number, data: any) => Promise<any>;
  deleteTabMethod?: (id: number) => Promise<any>;
  
  // Row Methods
  createRowMethod?: (data: any) => Promise<any>;
  updateRowMethod?: (id: number, data: any) => Promise<any>;
  deleteRowMethod?: (id: number) => Promise<any>;
  
  // Column Methods
  createColumnMethod?: (data: any) => Promise<any>;
  updateColumnMethod?: (id: number, data: any) => Promise<any>;
  deleteColumnMethod?: (id: number) => Promise<any>;
  
  // Value/Cell Methods
  createValueMethod?: (data: any) => Promise<any>;
  updateValueMethod?: (id: number, data: any) => Promise<any>;
}

export interface TabItem {
  id: number;
  name: string;
  order_index: number;
  is_active: boolean;
  // Styling
  text_color?: string;
  bg_color?: string;
  active_text_color?: string;
  active_bg_color?: string;
  // Category header
  category_header?: string;
  category_header_text_color?: string;
  category_header_bg_color?: string;
  category_header_border_color?: string;
  [key: string]: any;
}

export interface RowItem {
  id: number;
  name: string;
  order_index: number;
  is_active: boolean;
  tab_id?: number; // Links row to specific tab (fee_type_id)
  text_color?: string;
  bg_color?: string;
  border_color?: string;
  [key: string]: any;
}

export interface ColumnItem {
  id: number;
  name: string;
  order_index: number;
  is_active: boolean;
  tab_id?: number; // Links column to specific tab (fee_type_id)
  text_color?: string;
  bg_color?: string;
  border_color?: string;
  [key: string]: any;
}

export interface ValueItem {
  id: number;
  tab_id: number; // fee_type_id
  row_id: number; // category_id
  column_id: number; // semester_id
  value: string | null;
  text_color?: string;
  bg_color?: string;
  border_color?: string;
  [key: string]: any;
}

export interface InlineEditorProps {
  name: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  onNameChange: (value: string) => void;
  onTextColorChange: (value: string) => void;
  onBgColorChange: (value: string) => void;
  onBorderColorChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  placeholder?: string;
  defaultTextColor?: string;
  defaultBgColor?: string;
  defaultBorderColor?: string;
  showTextColor?: boolean;
  showBgColor?: boolean;
  showBorderColor?: boolean;
}

// Default configuration
export const DEFAULT_CONFIG: Form6Config = {
  tabsLabel: "Tabs",
  tabsSingularLabel: "Tab",
  rowsLabel: "Rows",
  rowsSingularLabel: "Row",
  columnsLabel: "Columns",
  columnsSingularLabel: "Column",
  categoryHeaderLabel: "Category",
  settingsIcon: "📝",
  tabsIcon: "📋",
  showTabStyling: true,
  showRowStyling: true,
  showColumnStyling: true,
  showCellStyling: true,
  showCategoryHeader: true,
};

